import { classnames } from 'tailwindcss-classnames';
import { LayoutPageSectionShell } from 'components/authorable/layout/LayoutPageSection/LayoutPageSection';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { Configure, useConnector } from 'react-instantsearch-hooks-web';
import { useState } from 'react';
import ProgramTypeFacet from 'components/helpers/AlgoliaSearch/Facets/NmdpSite/TcDirectory/ProgramTypeFacet';
import DiseaseFacet from 'components/helpers/AlgoliaSearch/Facets/NmdpSite/TcDirectory/DiseaseFacet';
import StateFacet from 'components/helpers/AlgoliaSearch/Facets/NmdpSite/TcDirectory/StateFacet';
import SurvivorshipFacet from 'components/helpers/AlgoliaSearch/Facets/NmdpSite/TcDirectory/SurvivorshipFacet';
import { Autocomplete } from 'components/helpers/AlgoliaSearch/Autocomplete';
import { TcSearchProps } from 'components/helpers/NmdpSite/TCDirectorySearch/_types';
import { useSearchDictionary } from 'components/helpers/AlgoliaSearch/util';
import connectStats, {
  StatsConnectorParams,
  StatsWidgetDescription,
} from 'instantsearch.js/es/connectors/stats/connectStats';
import Reset from 'components/helpers/AlgoliaSearch/Facets/NmdpSite/TcDirectory/ResetFacet';
import TcHitsSection from './Helpers/_HitsSection';
import fnNoOp from 'lib/util/no-op';

type TcDirectoryWrapperProps = {
  facets: JSX.Element;
  hits: JSX.Element;
  configSearch: JSX.Element;
  numberOfHits: number;
};
function useStats() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useConnector<StatsConnectorParams, StatsWidgetDescription>(connectStats as any, {});
}

const TcDirectoryWrapper = ({
  facets,
  hits,
  configSearch,
  numberOfHits,
}: TcDirectoryWrapperProps): JSX.Element => {
  // Fail out if we don't have any fields
  const dict = useSearchDictionary();
  return (
    <div
      data-component="authorable/nmdpsite/tcdirectorysearch"
      className={classnames('bg-white', 'tc-hits')}
    >
      <LayoutPageSectionShell theme="theme-white">
        <div>
          <div>{facets}</div>
        </div>
        <div
          className={classnames(
            'mx-auto',
            'md:border-b',
            'border-2',
            'border-gray-midGray',
            'max-w-screen-xl',
            'mx-auto',
            'my-8'
          )}
        />
        <div className={classnames('text-blue-navy', 'text-2xl', 'mb-8')}>
          {dict.results} ({numberOfHits})
        </div>
        {configSearch}
        {hits}
      </LayoutPageSectionShell>
    </div>
  );
};

const TcDirectoryWithSearch = ({ fields }: TcSearchProps): JSX.Element => {
  // Store program type selection as a paret state value
  const [type, setType] = useState('');

  const dict = useSearchDictionary();

  const [resetKey, setResetKey] = useState(0);

  const [txId, setTxId] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('txId') ?? '';
    }
    return '';
  });

  const [setResetAutocompleteCallback] = useState<() => void>(fnNoOp);

  const { nbHits } = useStats();

  const configureFilters = `type:tclisting`;

  const handleReset = () => {
    // Update URL to remove all params without a full page reload
    window.history.pushState({}, '', window.location.pathname);
    // Clear state and force remount of the search component tree
    setTxId('');
    setType('');
    setResetKey((k) => k + 1);
  };

  return (
    <div key={resetKey}>
      <TcDirectoryWrapper
        configSearch={<Configure analytics={false} filters={configureFilters} hitsPerPage={10} />}
        facets={
          <>
            <div className={classnames('md:flex', 'md:flex-row', 'my-4', 'md:w-11/12')}>
              <DiseaseFacet txId={txId} onTxIdChange={setTxId} diseases={fields?.diseases} />
              <ProgramTypeFacet type={type} setType={setType} />
              <div className={classnames('nrc-ct-instr', 'nmdp-tc-dir', 'my-4', 'md:basis-5/12')}>
                <div className={classnames('mb-0.5')}>
                  <span
                    className={classnames('text-solidBlack', 'text-base', 'font-semibold', 'mr-1')}
                  >
                    {dict.centerName}
                  </span>
                  <span className={classnames('text-gray-darkGray', 'text-base', 'font-regular')}>
                    {dict.optional}
                  </span>
                </div>
                <Autocomplete
                  detachedMediaQuery="none"
                  openOnFocus
                  asSearchBox
                  skipSuggestions
                  setResetCallback={setResetAutocompleteCallback}
                />
                <div
                  className={classnames('text-gray-darkGray', 'text-sm', 'font-regular', 'pb-1')}
                >
                  Enter any part of center name. This will also search doctor&apos;s names.
                </div>
              </div>
            </div>
            <div>
              <StateFacet />
            </div>
            <SurvivorshipFacet />
            <Reset onReset={handleReset} />
          </>
        }
        hits={<TcHitsSection txId={txId} nbHits={nbHits} />}
        numberOfHits={nbHits}
      />
    </div>
  );
};

const FakeFacets = (): JSX.Element => {
  return (
    <>
      <h5>Search Facets</h5>
    </>
  );
};
const FakeHits = (): JSX.Element => {
  return (
    <>
      <h5>TC Directory List will show here</h5>
    </>
  );
};

const TcDirectorySearch = ({ fields, ...props }: TcSearchProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const pageEditing = sitecoreContext.pageEditing === true;
  if (pageEditing) {
    return (
      <TcDirectoryWrapper
        configSearch={<></>}
        facets={<FakeFacets />}
        hits={<FakeHits />}
        numberOfHits={0}
      />
    );
  }
  return <TcDirectoryWithSearch fields={fields} {...props} />;
};

export default TcDirectorySearch;
